import React, { useState, useEffect } from "react";
import {
  Table,
  Tbody,
  Tr,
  Td,
  Th,
  Thead,
  TableContainer,
  Box,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { BASE_URL } from "../../../Globalapi/Globalapi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const ModelTest = () => {
  const [academicId, setAcademicId] = useState(
    sessionStorage.getItem("Academic")
  );
  const [marksData, setMarksData] = useState([]);
  const [examCategories, setExamCategories] = useState([]);
  const { user_id } = useParams();
  useEffect(() => {
    const handleStorageChange = () => {
      setAcademicId(sessionStorage.getItem("Academic"));
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  // Fetch Marks Data
  const fetchMarks = () => {
    fetch(`${BASE_URL}/student/exam_result_graph.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ pass: user_id }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          console.log("Raw Marks Data:", data.data);

          // Filter data based on academic year
          const filteredDetails = data.data.filter(
            (detail) => detail.academic_year_id === academicId
          );

          setMarksData(filteredDetails);
          console.log("Filtered Marks Data:", filteredDetails);
        } else {
          console.error("No data found or error in API response.");
          setMarksData([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching marks data:", error);
      });
  };

  const fetchExamCategories = () => {
    fetch(`${BASE_URL}/admin/exam/exam_category.php`)
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setExamCategories(data.data);
          console.log("Fetched Exam Categories:", data.data);
        } else {
          console.error("No category data found or error in API response.");
        }
      })
      .catch((error) => {
        console.error("Error fetching exam categories:", error);
      });
  };

  useEffect(() => {
    if (academicId && user_id) {
      fetchMarks();
    }
    fetchExamCategories();
  }, [academicId, user_id]);

  // Filter marks data by exam categories
  const filteredExamCategories = examCategories.filter((category) =>
    marksData.some((exam) => exam.exam_category_id === category.id)
  );

  // Get subjects dynamically from marks data
  const subjects = Array.from(new Set(marksData.map((exam) => exam.subject)));

  // Organize marks data by subject and exam category
  const marksBySubject = subjects.map((subject) => {
    const subjectData = {};
    filteredExamCategories.forEach((category) => {
      const exam = marksData.find(
        (mark) =>
          mark.exam_category_id === category.id && mark.subject === subject
      );
      subjectData[category.category] = exam || {
        written_mark: "-",
        internal_mark: "-",
        student_scored_marks: "-",
      };
    });
    return { subject, data: subjectData };
  });

  // Chart Data for the Graph
  const chartData = {
    labels: filteredExamCategories.map((category) => category.category),
    datasets: subjects.map((subject, index) => ({
      label: subject,
      data: filteredExamCategories.map((category) => {
        const exam = marksData.find(
          (mark) =>
            mark.exam_category_id === category.id && mark.subject === subject
        );
        return exam ? parseFloat(exam.student_scored_marks) : 0;
      }),
      borderColor: `rgba(${index * 50 + 100}, 99, 132, 1)`,
      backgroundColor: `rgba(${index * 50 + 100}, 99, 132, 0.2)`,
      borderWidth: 2,
      pointRadius: 5,
      pointBackgroundColor: "white",
      pointBorderColor: `rgba(${index * 50 + 100}, 99, 132, 1)`,
      fill: true,
      tension: 0.4,
    })),
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 20,
          padding: 20,
        },
      },
      title: {
        display: true,
        text: "Average Marks per Term by Subject",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Average Marks",
        },
      },
      x: {
        title: {
          display: true,
          text: "Exam Categories",
        },
      },
    },
  };

  return (
    <Tabs variant="enclosed" colorScheme="teal">
      <TabList>
        <Tab>Annual Report Table</Tab>
        <Tab>Graph</Tab>
      </TabList>

      <TabPanels>
        <TabPanel>
          <TableContainer padding="20px" maxWidth="800px" margin="0 auto">
            <Table
              sx={{
                "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
              }}
              size="sm"
              border="1px solid #d4c2c2"
              width="100%"
              tableLayout="fixed"
            >
              <Thead>
                <Tr>
                  <Th
                    textAlign="center"
                    verticalAlign="middle"
                    border="1px solid #d4c2c2"
                    borderBottom="none"
                  >
                    SUBJECTS
                  </Th>
                  {filteredExamCategories.map((category) => (
                    <Th
                      key={category.id}
                      colSpan={3}
                      textAlign="center"
                      verticalAlign="middle"
                      border="1px solid #d4c2c2"
                    >
                      {category.category}
                    </Th>
                  ))}
                </Tr>
                <Tr>
                  <Th
                    textAlign="center"
                    borderLeft="1px solid #d4c2c2"
                    borderBottom="1px solid #d4c2c2"
                  ></Th>
                  {filteredExamCategories.map((category) => (
                    <React.Fragment key={category.id}>
                      <Th textAlign="center" border="1px solid #d4c2c2">
                        WE
                      </Th>
                      <Th textAlign="center" border="1px solid #d4c2c2">
                        IA
                      </Th>
                      <Th textAlign="center" border="1px solid #d4c2c2">
                        Total
                      </Th>
                    </React.Fragment>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {marksBySubject.map((item, index) => (
                  <Tr key={index}>
                    <Td
                      verticalAlign="middle"
                      border="1px solid #d4c2c2"
                      textAlign="center"
                      fontWeight="bold"
                    >
                      {item.subject}
                    </Td>
                    {filteredExamCategories.map((category) => {
                      const data = item.data[category.category] || {};
                      return (
                        <React.Fragment key={category.id}>
                          <Td border="1px solid #d4c2c2" textAlign="center">
                            {data.written_mark || "-"}
                          </Td>
                          <Td border="1px solid #d4c2c2" textAlign="center">
                            {data.internal_mark || "-"}
                          </Td>
                          <Td border="1px solid #d4c2c2" textAlign="center">
                            {data.student_scored_marks || "-"}
                          </Td>
                        </React.Fragment>
                      );
                    })}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </TabPanel>
        <TabPanel>
          <Box width="600px" mx="auto">
            <Line data={chartData} options={chartOptions} />
          </Box>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default ModelTest;
